import { UserContext, type UserContextProps, useUser } from '@faceup/member'
import { useFeatureFlag } from '@faceup/posthog'
import {
  ChargebeeSubscriptionStatus,
  Feature,
  FrontendPermissionType,
  Institution,
} from '@faceup/utils'
import { useContext } from 'react'
import { useInstitution } from '../providers'
import { useEntitlement } from './useEntitlement'
import { usePartner } from './usePartner'

export type UseAccessRightsReturn = {
  isAllowedSettingsAccess: boolean
  isAllowedSSO: boolean
  isAllowedWebhooks: boolean
  isAllowedApi: boolean
  isVisibleApi: boolean
  isVisibleWhitelabel: boolean
  isAllowedWhitelabel: boolean
  isVisibleEnableAccessKey: boolean
  isAllowedE2EE: boolean
  areReportsNeedingAttentionVisible: boolean
  areVisibleWebhooks: boolean
  isBioVisible: boolean
  isAllowedMultipleReportSources: boolean
  isVisiblePdfPasswordAlert: boolean
  isAllowedStatistics: boolean
  isAllowedInvestigations: boolean
  isAllowedNewInvestigations: boolean
  isAllowedDisclosures: boolean
  isAllowedCustomDocuments: boolean
  isAllowedCustomizationEdit: boolean
  isVisibleForPermission: Record<FrontendPermissionType, boolean>
  isPartnerAdministration: boolean
  permissionsWithInstitutionIds: Record<FrontendPermissionType, string[]>
  isAllowedIntegration: boolean
  isAllowedReportsBadge: boolean
  isAllowedCreateAutomatedHotline: boolean
  isAllowedCreateLiveHotline: boolean
  isAllowedCreateAiHotline: boolean
  areReportAttachmentsVisible: boolean
  areVisibleDataManagementEmployees: boolean
  areVisibleReportEmployees: boolean
  isAllowedIpAllowlist: boolean
  isSettingsVisible: boolean
  isAllowedEmployeeManagement: boolean
}

type FnUseAccessRights = () => UseAccessRightsReturn

export const useAccessRights: FnUseAccessRights = () => {
  const {
    data: { usePaidVersion, institution, subscriptionStatus, hasEmployees },
  } = useInstitution({
    // Should be better separated
    __unsafeSkipCheckIfNotWrappedInInstitutionProvider: true,
  })
  const { hasPermissions, hasAtLeastOnePermission } = usePermissions()
  const { isPartnerAdministration } = usePartner()
  const { isFeatureFlagEnabled } = useFeatureFlag()
  const { viewer } = useContext(UserContext)
  const permissionsWithInstitutionIds = usePermissionsWithInstitutionIds(viewer)
  const isEntitlementEnabled = useEntitlement()

  const isActive = [
    ChargebeeSubscriptionStatus.Active,
    ChargebeeSubscriptionStatus.NonRenewing,
  ].includes(subscriptionStatus ?? ChargebeeSubscriptionStatus.Cancelled)

  /*
   * isAllowed - has access to item (e.g. to persist data) - to show buy higher pricing plan info
   * isVisible - is item visible or fully hidden in page
   */
  const isSchool = institution === Institution.School
  const isFreeOrNotActiveInstitution = !usePaidVersion || !isActive

  const isVisibleForPermission = Object.values(FrontendPermissionType).reduce(
    (acc, permission) => ({
      ...acc,
      [permission]: hasPermissions(permission),
    }),
    {} as Record<FrontendPermissionType, boolean>
  )

  return {
    isAllowedSettingsAccess: isVisibleForPermission.SettingsAccess,
    isAllowedSSO: isEntitlementEnabled(Feature.Sso),
    isAllowedWebhooks: isEntitlementEnabled(Feature.Webhooks),
    isAllowedApi: isEntitlementEnabled(Feature.Api),
    isVisibleApi: isVisibleForPermission.SettingsAccess && !(isSchool && !usePaidVersion),
    isVisibleWhitelabel: !isSchool && usePaidVersion && isVisibleForPermission.SettingsAccess,
    isAllowedWhitelabel: isEntitlementEnabled(Feature.Whitelabel),
    isAllowedInvestigations: isEntitlementEnabled(Feature.Investigations),
    isAllowedNewInvestigations:
      isEntitlementEnabled(Feature.NewInvestigations) ||
      isFeatureFlagEnabled('investigations-early-access'),
    isAllowedDisclosures: isEntitlementEnabled(Feature.Disclosures),
    isAllowedCustomDocuments: isEntitlementEnabled(Feature.CustomDocuments),
    isVisibleEnableAccessKey: isSchool,
    isAllowedE2EE: usePaidVersion,
    areReportsNeedingAttentionVisible:
      isEntitlementEnabled(Feature.CaseManagement) && isVisibleForPermission.ReportAccess,
    areVisibleWebhooks: isVisibleForPermission.SettingsAccess && !(isSchool && !usePaidVersion),
    isBioVisible: usePaidVersion,
    isAllowedMultipleReportSources: usePaidVersion && isActive,
    isVisiblePdfPasswordAlert: !usePaidVersion,
    isAllowedStatistics: isVisibleForPermission.Analytics,
    isAllowedCustomizationEdit: usePaidVersion && isVisibleForPermission.ManageReportingChannels,
    isVisibleForPermission,
    isPartnerAdministration,
    permissionsWithInstitutionIds,
    isAllowedIntegration:
      isEntitlementEnabled(Feature.Integrations) && isVisibleForPermission.SettingsAccess,
    isAllowedReportsBadge: isEntitlementEnabled(Feature.CaseManagement),
    isAllowedCreateAutomatedHotline: !isFreeOrNotActiveInstitution,
    isAllowedCreateLiveHotline: !isFreeOrNotActiveInstitution,
    isAllowedCreateAiHotline: !isFreeOrNotActiveInstitution,
    areReportAttachmentsVisible: isEntitlementEnabled(Feature.CaseManagement),
    areVisibleDataManagementEmployees: hasEmployees,
    areVisibleReportEmployees: hasEmployees,
    isAllowedIpAllowlist: isEntitlementEnabled(Feature.IpAllowlist),
    isSettingsVisible: hasAtLeastOnePermission(
      FrontendPermissionType.SettingsAccess,
      FrontendPermissionType.ManageUsers,
      FrontendPermissionType.ManageOrganizationalUnits,
      FrontendPermissionType.BillingAccess
    ),
    isAllowedEmployeeManagement: isEntitlementEnabled(Feature.EmployeeManagement),
  }
}

const usePermissions = () => {
  const {
    data: { permissions },
  } = useInstitution({
    // Should be separated from partner/institution
    // It's not auth related thing
    __unsafeSkipCheckIfNotWrappedInInstitutionProvider: true,
  })
  const user = useUser()

  const enabledPermissionsTypes = permissions
    .filter(permission => permission.enabled)
    .map(permission => permission.type)

  const hasPermissions = (...requiredPermissions: FrontendPermissionType[]) => {
    if (user.application === 'kredenc') {
      return true
    }

    return requiredPermissions.every(requiredPermission =>
      enabledPermissionsTypes.includes(requiredPermission)
    )
  }

  const hasAtLeastOnePermission = (...requiredPermissions: FrontendPermissionType[]) => {
    if (user.application === 'kredenc') {
      return true
    }

    return requiredPermissions.some(requiredPermission =>
      enabledPermissionsTypes.includes(requiredPermission)
    )
  }

  return {
    hasPermissions,
    hasAtLeastOnePermission,
  }
}

const usePermissionsWithInstitutionIds = (viewer: UserContextProps['viewer']) => {
  const organizationalUnitsWithAccess = new Set(viewer?.partnerCompanyIds)
  const partnerInstitutionIds = new Set(viewer?.partnerInstitutionIds)

  return Object.values(FrontendPermissionType).reduce(
    (acc, permission) => ({
      ...acc,
      [permission]:
        viewer?.keys.partnerPermissions
          ?.filter(
            partnerPermission => partnerPermission.type === permission && partnerPermission.enabled
          )
          .map(permission => permission.motherId)
          .filter(id => {
            if (permission === FrontendPermissionType.Analytics) {
              // for analytics it's important only to have InstitutionAccess
              return partnerInstitutionIds.has(id)
            }
            return organizationalUnitsWithAccess.has(id)
          }) ?? [],
    }),
    {} as Record<FrontendPermissionType, string[]>
  )
}
